import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Sheet } from '@/components/common/Sheet';
import { ContestoStatoAvversari, type StatoAvversariApi } from '@/components/hub/panels/storico/contestoStato';
import { SfidaSheetDaCatalogo } from '@/components/social/SfidaSheet';
import { useHaptics } from '@/hooks/useHaptics';
import { useSfidaDiPiattaforma } from '@/hooks/useSfidaDiPiattaforma';
import { dopoGesto, type Gesto, type StatoSocial } from '@/lib/qgammon/storicoPartite';
import type { FriendshipDTO } from '@/types/social';
import {
  useAcceptFriendshipMutation,
  useFollowUserMutation,
  useLazyResolveProfilesBySubsQuery,
  useRemoveFriendshipMutation,
  useRequestFriendshipMutation,
  useUnfollowUserMutation,
} from '@/services/socialApi';

/**
 * UNA sola sorgente di stato social per avversario (per `sub`), per tutta la lista.
 *
 * Il lotto di QSocials e' per PAGINA dello storico, e lo stesso avversario compare in righe di pagine diverse: se ogni
 * riga tenesse il suo stato, un gesto ne muoverebbe una sola e le altre direbbero ancora il contrario. Il lotto dice
 * com'era ALLA LETTURA; qui stanno i cambiamenti fatti DOPO (per `sub`), e `statoDi` li preferisce. Un gesto scrive
 * solo a scrittura RIUSCITA: niente ottimismo — un «Segui» che il server ha rifiutato non deve restare acceso.
 *
 * Qui stanno anche le cose che una riga non deve possedere: la serratura della sfida (UNA lettura per lista, lo schema
 * di `ElencoConversazioni`, #526), il foglio della sfida (montato SOLO mentre e' aperto: legge `/v1/games`) e la conferma
 * di «Rimuovi amico».
 */

interface Bersaglio {
  sub: string;
  nome: string;
  dalLotto: StatoSocial | null;
}

export function StatoAvversariProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('backgammon');
  const navigate = useNavigate();
  const haptics = useHaptics();
  // UNA lettura per lista, non una per riga.
  const sfidaAccesa = useSfidaDiPiattaforma();

  // `null` = il lotto riletto dopo un errore NON ha piu' quel sub (un server senza la #29 di QSocials omette chi ha bloccato
  // il lettore o non ha profilo; dalla #29 ogni sub ha una voce, e chi blocca arriva come uno sconosciuto, `none`): niente icone.
  const [cambiati, setCambiati] = useState<Readonly<Record<string, StatoSocial | null>>>({});
  const [inVoloSet, setInVoloSet] = useState<ReadonlySet<string>>(() => new Set());
  const [fallitiSet, setFallitiSet] = useState<ReadonlySet<string>>(() => new Set());
  // Lo stato che le righe mostrano ADESSO, per confrontarlo con quello riletto dopo un errore (un updater non si legge di fuori).
  const cambiatiRef = useRef(cambiati);
  useEffect(() => {
    cambiatiRef.current = cambiati;
  }, [cambiati]);
  // Il guardiano sincrono: fra due tocchi ravvicinati lo stato React non e' ancora cambiato.
  const inVoloRef = useRef<Set<string>>(new Set());

  const [daSfidare, setDaSfidare] = useState<Bersaglio | null>(null);
  const [daRimuovere, setDaRimuovere] = useState<Bersaglio | null>(null);

  const [follow] = useFollowUserMutation();
  const [unfollow] = useUnfollowUserMutation();
  const [richiedi] = useRequestFriendshipMutation();
  const [accetta] = useAcceptFriendshipMutation();
  const [rimuovi] = useRemoveFriendshipMutation();
  const [rileggiLotto] = useLazyResolveProfilesBySubsQuery();

  /** Fa la scrittura. Per richiedi/accetta torna l'amicizia che dice il SERVER: da li' si legge lo stato, non si calcola. */
  const scrivi = useCallback(
    async (sub: string, gesto: Gesto): Promise<FriendshipDTO | undefined> => {
      switch (gesto) {
        case 'segui':
          await follow({ followedUserId: sub }).unwrap();
          return undefined;
        case 'nonSeguire':
          await unfollow({ followedUserId: sub }).unwrap();
          return undefined;
        case 'richiedi':
          return richiedi({ friendUserId: sub }).unwrap();
        case 'accetta':
          return accetta({ friendUserId: sub }).unwrap();
        case 'annullaRichiesta':
        case 'rimuovi':
          await rimuovi({ friendUserId: sub }).unwrap();
          return undefined;
        default:
          // `profilo` e `sfida` non scrivono: non arrivano mai qui.
          return undefined;
      }
    },
    [follow, unfollow, richiedi, accetta, rimuovi],
  );

  const scriviEAggiorna = useCallback(
    async (b: Bersaglio, gesto: Gesto) => {
      if (inVoloRef.current.has(b.sub)) return;
      inVoloRef.current.add(b.sub);
      setInVoloSet(new Set(inVoloRef.current));
      setFallitiSet((p) => {
        if (!p.has(b.sub)) return p;
        const n = new Set(p);
        n.delete(b.sub);
        return n;
      });
      haptics.impact('light');
      try {
        const amicizia = await scrivi(b.sub, gesto);
        // Solo a scrittura RIUSCITA, e sullo stato di ADESSO (il gesto di un'altra riga puo' averlo mosso).
        setCambiati((p) => {
          const base = b.sub in p ? p[b.sub] : b.dalLotto;
          return base ? { ...p, [b.sub]: dopoGesto(base, gesto, amicizia) } : p;
        });
        haptics.notification('success');
      } catch {
        // La scrittura e' fallita, e lo stato che la riga mostra puo' essere VECCHIO: un 409 (`already_friends`,
        // `already_pending`) dice proprio che il server sa una cosa che noi no. Si RILEGGE il lotto per questo sub e si
        // mostra quello; senza, lo stesso tocco fallirebbe di nuovo fino al prossimo montaggio.
        let cambiato = false;
        try {
          const risultati = await rileggiLotto({ subs: [b.sub] }, false).unwrap();
          const vero = risultati.find((r) => r.sub === b.sub);
          const nuovo: StatoSocial | null = vero ? { relation: vero.relation, following: vero.following } : null;
          const prima = b.sub in cambiatiRef.current ? cambiatiRef.current[b.sub] : b.dalLotto;
          cambiato = prima?.relation !== nuovo?.relation || prima?.following !== nuovo?.following;
          setCambiati((p) => ({ ...p, [b.sub]: nuovo }));
        } catch {
          // Nemmeno la rilettura e' andata: lo stato resta com'era e lo dice la frase d'errore.
        }
        // Se la rilettura ha mostrato una cosa diversa la riga dice gia' la verita': la frase sarebbe di troppo.
        if (!cambiato) setFallitiSet((p) => new Set(p).add(b.sub));
        haptics.notification('error');
      } finally {
        inVoloRef.current.delete(b.sub);
        setInVoloSet(new Set(inVoloRef.current));
      }
    },
    [haptics, scrivi, rileggiLotto],
  );

  const esegui = useCallback<StatoAvversariApi['esegui']>(
    (sub, nome, gesto, dalLotto) => {
      const b: Bersaglio = { sub, nome, dalLotto };
      if (gesto === 'profilo') {
        haptics.selection();
        navigate(`/profile/${encodeURIComponent(sub)}`);
      } else if (gesto === 'sfida') {
        // Il tocco da solo non scrive niente: la sfida parte solo dalla scheda.
        haptics.selection();
        setDaSfidare(b);
      } else if (gesto === 'rimuovi') {
        haptics.selection();
        setDaRimuovere(b);
      } else {
        void scriviEAggiorna(b, gesto);
      }
    },
    [haptics, navigate, scriviEAggiorna],
  );

  const api = useMemo<StatoAvversariApi>(
    () => ({
      statoDi: (sub, dalLotto) => (sub in cambiati ? cambiati[sub] : dalLotto),
      inVolo: (sub) => inVoloSet.has(sub),
      fallito: (sub) => fallitiSet.has(sub),
      sfidaAccesa,
      esegui,
    }),
    [cambiati, inVoloSet, fallitiSet, sfidaAccesa, esegui],
  );

  const confermaRimozione = () => {
    if (!daRimuovere) return;
    const b = daRimuovere;
    setDaRimuovere(null);
    void scriviEAggiorna(b, 'rimuovi');
  };

  return (
    <ContestoStatoAvversari.Provider value={api}>
      {children}
      {/* Montato SOLO mentre e' aperto: legge `/v1/games` e prepara l'invio, e la lista non deve pagarli prima. */}
      {daSfidare && (
        <SfidaSheetDaCatalogo
          avversario={daSfidare.nome}
          amicoId={daSfidare.sub}
          preselezionaBackgammon
          onClose={() => setDaSfidare(null)}
        />
      )}
      <Sheet open={daRimuovere !== null} onClose={() => setDaRimuovere(null)} label={t('storico.rimuovi.titolo', { name: daRimuovere?.nome ?? '' })}>
        <div className="flex flex-col gap-3 px-5 pb-6 pt-2">
          <p className="text-lg font-extrabold text-fg">{t('storico.rimuovi.titolo', { name: daRimuovere?.nome ?? '' })}</p>
          <p className="text-sm text-fg">{t('storico.rimuovi.testo', { name: daRimuovere?.nome ?? '' })}</p>
          <button
            type="button"
            data-testid="storico-rimuovi-conferma"
            onClick={confermaRimozione}
            className="mt-2 h-12 rounded-2xl bg-danger font-extrabold text-white"
          >
            {t('storico.rimuovi.conferma')}
          </button>
          <button
            type="button"
            onClick={() => setDaRimuovere(null)}
            className="h-12 rounded-2xl bg-bg-secondary font-bold text-fg"
          >
            {t('storico.rimuovi.annulla')}
          </button>
        </div>
      </Sheet>
    </ContestoStatoAvversari.Provider>
  );
}
